// src/pages/AddUserPage.js
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Card,
  CardContent,
  Avatar,
  CircularProgress,
  Switch,
  Divider,
  IconButton,
  Stack,
  Autocomplete,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import API from "../utils/api";
import { toast } from "react-toastify";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

// NEW IMPORTS
import { validatePassword } from "../utils/validatePassword";
import PasswordRequirements from "../components/PasswordRequirements";
import ConfirmDialog from "../components/ConfirmDialog";
import useTerritoryAssignment from "../hooks/useTerritoryAssignment";
import useAuth from "../hooks/useAuth";
import DisabledButtonWrapper from "../components/DisabledButtonWrapper";
import { getUserInitials } from "../utils/userInitials";
import { formatPhone } from "../utils/formatPhone";
import {
  CircleCheck,
  Mail,
  MessageSquare,
  Map,
  Shield,
  UsersRound,
  UserRound,
  User,
} from "lucide-react";


function normalizeNamePart(value) {
  return (value || "")
    .normalize("NFKD")
    .replace(/[^\x00-\x7F]/g, "")
    .replace(/[^A-Za-z\s-]/g, " ")
    .trim();
}

function toCapitalizedWords(value) {
  return normalizeNamePart(value)
    .split(/[\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join("");
}

function buildNormalizedUsername(firstName, lastName) {
  const firstInitial = normalizeNamePart(firstName).charAt(0).toUpperCase();
  const normalizedLast = toCapitalizedWords(lastName);
  if (!firstInitial || !normalizedLast) return "";
  return `${firstInitial}${normalizedLast}`;
}

export default function AddUserPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { isReadOnly } = useAuth();
  const savingRef = useRef(false);
  const [saving, setSaving] = useState(false);

  const [groups, setGroups] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [users, setUsers] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [confirmSalesDirector, setConfirmSalesDirector] = useState(false);
  const [pendingSalesDirectorChange, setPendingSalesDirectorChange] = useState(false);
  const [adminExists, setAdminExists] = useState(false);
  const [showAdminWarning, setShowAdminWarning] = useState(false);

  const [passwordError, setPasswordError] = useState("");

const [formData, setFormData] = useState({
  first_name: "",
  last_name: "",
  username: "",
  email: "",
  phone: "",
  password: "",
  groups: [],
  is_active: true,
});


  

const {
  assignToTerritory,
  setAssignToTerritory,
  selectedTerritories,
  setSelectedTerritories,
} = useTerritoryAssignment({
  territories,
});

  // 🔹 Check if admin exists
  const checkAdminExists = (usersList) => {
    const hasAdmin = usersList.some(user => {
      if (!user.groups || !Array.isArray(user.groups)) return false;
      
      // Check if user has Admin role
      const userGroupNames = user.groups.map(g => 
        typeof g === 'object' ? g.name : g
      );
      return userGroupNames.includes("Admin");
    });
    
    setAdminExists(hasAdmin);
    return hasAdmin;
  };

  // 🔹 Fetch groups + territories + users
  useEffect(() => {
    (async () => {
      try {
        const [gRes, tRes, usersRes] = await Promise.all([
          API.get("/groups/"),
          API.get("/territories/"),
          API.get("/users/"),
        ]);

        setGroups(gRes.data || []);
        setTerritories(tRes.data || []);
        setUsers(usersRes.data || []);
        
        // Check if admin exists
        checkAdminExists(usersRes.data || []);
      } catch (error) {
        console.error("Init fetch error:", error);
        toast.error("Failed to load groups/territories.");
      }
    })();
  }, []);

  // 🔹 Validation gate for Save button
  const canSave = useMemo(() => {
    const { first_name, last_name, username, email, password, groups } = formData;

    return (
      first_name.trim() &&
      last_name.trim() &&
      username.trim() &&
      email.trim() &&
      password.trim() &&
      groups.length > 0 &&
      passwordError === ""
    );
  }, [formData, passwordError]);

  // 🔹 Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "password") {
      setFormData((prev) => ({ ...prev, password: value }));
      const validationErrors = validatePassword(value);
      setPasswordError(validationErrors.length ? validationErrors.join("\n") : "");
      return;
    }

    if (name === "phone") {
      setFormData((prev) => ({ ...prev, phone: formatPhone(value) }));
      return;
    }

    if (name === "groups") {
      setFormData((prev) => ({ ...prev, groups: [Number(value)] }));
      return;
    }

    if (name === "first_name" || name === "last_name") {
      setFormData((prev) => {
        const next = { ...prev, [name]: value };
        next.username = buildNormalizedUsername(next.first_name, next.last_name);
        return next;
      });
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleToggleActive = (e) => {
    setFormData((prev) => ({ ...prev, is_active: e.target.checked }));
  };

  const handleSubmit = async () => {
    if (isReadOnly || savingRef.current) return; // Kill the click for read-only users and a second save
    
    if (!canSave) return toast.warn("Please complete all required fields.");

    const pwErrors = validatePassword(formData.password);
    if (pwErrors.length) {
      setPasswordError(pwErrors.join("\n"));
      return;
    }
    const territoryIds = assignToTerritory
  ? selectedTerritories.map(t => t.id)
  : [];


    savingRef.current = true;
    setSaving(true);
    try {
      const payload = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        username: formData.username.trim(),
        email: formData.email.trim(),
        phone: formatPhone(formData.phone),
        password: formData.password,
        groups: formData.groups.map(Number),
        is_active: !!formData.is_active,
        
	territories: territoryIds,
  	primary_territory: territoryIds.length > 0 ? territoryIds[0] : null,

 	 // ⏳ temporary legacy
 	 territory_id: territoryIds.length > 0 ? territoryIds[0] : null,
      };

      await API.post("/users/", payload);
      toast.success("User created successfully!");
      navigate("/users");
    } catch (error) {
      savingRef.current = false;
      setSaving(false);
      console.error("Error creating user:", error?.response?.data || error);
      const emailErr = error?.response?.data?.email;
      const emailMsg = Array.isArray(emailErr) ? emailErr[0] : emailErr;
      toast.error(emailMsg || error?.response?.data?.detail || "Failed to create user.");
    }
  };

  const handleCancel = () => navigate("/users");

  const fullName = `${formData.first_name} ${formData.last_name}`.trim() || "New User";
  const selectedRoleName = formData.groups.length > 0 
    ? groups.find(g => g.id === formData.groups[0])?.name || "No role assigned"
    : "No role assigned";

  return (
    <Box sx={{ p: 3, px: { xs: 1, sm: 3 } }}>
<Box sx={{ maxWidth: 1440, mx: "auto", width: "100%" }}>
      {/* -------------------------------------------------------------PROFILE HEADER */}
      <Card
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
        }}
      >
        <Box 
          display="flex" 
          justifyContent="space-between" 
          alignItems={{ xs: "flex-start", md: "center" }}
          flexDirection={{ xs: "column", md: "row" }}
          gap={{ xs: 2, md: 0 }}
        >
          <Box display="flex" alignItems="center" gap={2} width={{ xs: "100%", md: "auto" }}>
            <Avatar sx={{ width: 64, height: 64, bgcolor: "primary.main", color: "#fff" }}>
              {getUserInitials(
                {
                  first_name: formData.first_name,
                  last_name: formData.last_name,
                  full_name: fullName,
                },
                "NU"
              )}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {fullName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedRoleName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Creating new user
              </Typography>
            </Box>
          </Box>

          <Stack 
            direction={{ xs: "column", md: "row" }} 
            spacing={1} 
            width={{ xs: "100%", md: "auto" }}
          >
            <Button 
              startIcon={<ArrowBackIcon />} 
              variant="outlined"
              onClick={handleCancel}
              fullWidth={isMobile}
              sx={{ whiteSpace: "nowrap" }}
            >
              Back
            </Button>
            {/* -------------------------------------------------------------Save button moved to bottom on mobile */}
            {!isMobile && (
              <DisabledButtonWrapper 
                isDisabled={(isReadOnly || !canSave) && !saving}
                tooltipText={!canSave ? "Please complete all required fields" : "Read-only mode: This action is disabled"}
              >
                <Button 
                  startIcon={saving ? <CircularProgress size={16} thickness={5} color="inherit" /> : <SaveIcon />}
                  variant="outlined"
                  color="success"
                  onClick={handleSubmit}
                  disabled={isReadOnly || !canSave || saving}
                  sx={{ 
                    whiteSpace: "nowrap",
                    opacity: (isReadOnly || !canSave) ? 0.6 : 1,
                    cursor: saving ? "wait" : (isReadOnly || !canSave) ? "not-allowed" : "pointer",
                    visibility: 'visible',
                    display: 'inline-flex',
                  }}
                >
                  {saving ? "Saving..." : "Save User"}
                </Button>
              </DisabledButtonWrapper>
            )}
          </Stack>
        </Box>
      </Card>

      {/*------------------------------------------------------------------ MAIN GRID */}
      <Box
        display="grid"
        gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr 1fr" }}
        gap={3}
      >
        {/* ---------------------------------------------------------------PROFILE INFO */}
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between">
              <Typography fontWeight={700}>Profile Information</Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" mt={2}>
              Short Bio:
            </Typography>

            <Divider sx={{ my: 2 }} />

            <TextField
              label="First Name"
              value={formData.first_name}
              onChange={handleChange}
              name="first_name"
              fullWidth
              size="small"
              sx={{ mb: 2 }}
            />
            <TextField
              label="Last Name"
              value={formData.last_name}
              onChange={handleChange}
              name="last_name"
              fullWidth
              size="small"
              sx={{ mb: 2 }}
            />
            <TextField
              label="Username"
              value={formData.username}
              onChange={handleChange}
              name="username"
              fullWidth
              size="small"
              inputProps={{ tabIndex: -1 }}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <LockOutlinedIcon sx={{ fontSize: 16, opacity: 0.6 }} />
                ),
              }}
              helperText="Auto-generated from first and last name"
              sx={{
                mb: 2,
                "& .MuiInputBase-root": {
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.04)"
                      : "action.disabledBackground",
                  opacity: 0.85,
                },
                "& .MuiInputBase-input": {
                  WebkitTextFillColor: "currentColor",
                  cursor: "not-allowed",
                },
              }}
            />
            <TextField
              label="Password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleChange}
              name="password"
              fullWidth
              size="small"
              sx={{ mb: 1 }}
              InputProps={{
                endAdornment: (
                  <IconButton
                    size="small"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                ),
              }}
            />
            <PasswordRequirements password={formData.password} />
            <Box sx={{ mt: 1.5 }} />
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              name="email"
              fullWidth
              size="small"
              sx={{ mb: 2 }}
            />
            <TextField
              label="Mobile"
              type="tel"
              name="phone"
              placeholder="(xxx) xxx-xxxx"
              value={formatPhone(formData.phone)}
              onChange={handleChange}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
            />
          </CardContent>
        </Card>

        {/* ---------------------------------------------------------------------PLATFORM SETTINGS */}
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography fontWeight={700} mb={2}>
              Platform Settings
            </Typography>

            <Typography variant="caption" color="text.secondary">
              ACCOUNT
            </Typography>

            <SettingRow 
              label="Active" 
              icon={<CircleCheck size={19} strokeWidth={2} />}
              iconColor="#16a34a"
              checked={formData.is_active}
              onChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
            />
            <SettingRow 
              label="Send system emails" 
              icon={<Mail size={19} strokeWidth={2} />}
              iconColor="#2563eb"
              checked={false}
              onChange={() => {}}
            />
            <SettingRow 
              label="Send System SMS" 
              icon={<MessageSquare size={19} strokeWidth={2} />}
              iconColor="#2563eb"
              checked={false}
              onChange={() => {}}
            />

            <Divider sx={{ my: 2 }} />

            <Typography variant="caption" color="text.secondary">
              TERRITORY SETTING
            </Typography>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                minHeight: 44,
                py: 0.55,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Box sx={{ display: "flex", alignItems: "center", color: "#7c3aed" }}>
                  <Map size={19} strokeWidth={2} />
                </Box>
                <Typography variant="body2">Assign to territory</Typography>
              </Box>
              <Switch
  checked={assignToTerritory}
  onChange={(e) => setAssignToTerritory(e.target.checked)}
/>

            </Box>

          <Box my={2}>
  <Autocomplete
    multiple
    disabled={!assignToTerritory}
    options={territories}
    getOptionLabel={(option) => option.name || ""}
    value={selectedTerritories}
    onChange={(e, newValue) => setSelectedTerritories(newValue)}
    renderInput={(params) => (
      <TextField
        {...params}
        label="Select Territories"
        placeholder="Choose territories"
        size="small"
        sx={{
          "& .MuiInputBase-root": {
            fontSize: "0.875rem",
          },
          "& .MuiInputLabel-root": {
            fontSize: "0.875rem",
          },
        }}
      />
    )}
  />
</Box>


          </CardContent>
        </Card>

        {/* -----------------------------------------------------------------------PERMISSIONS */}
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography fontWeight={700} mb={2}>
              Permissions
            </Typography>

            <SettingRow 
              label="Admin" 
              icon={<Shield size={19} strokeWidth={2} />}
              iconColor="#ef4444"
              checked={formData.groups.length > 0 && groups.find(g => g.id === formData.groups[0])?.name === "Admin"}
              onChange={(checked) => {
                if (checked) {
                  // Check if admin already exists
                  if (adminExists) {
                    // Show warning dialog
                    setShowAdminWarning(true);
                    return; // Don't proceed with the change
                  }
                  
                  // No admin exists, proceed normally
                  const adminGroup = groups.find(g => g.name === "Admin");
                  if (adminGroup) {
                    setFormData(prev => ({ ...prev, groups: [adminGroup.id] }));
                  }
                } else {
                  // Turning OFF - no check needed
                  setFormData(prev => ({ ...prev, groups: [] }));
                }
              }}
            />
            <SettingRow 
              label="Sales Director" 
              icon={<UsersRound size={19} strokeWidth={2} />}
              iconColor="#7c3aed"
              checked={formData.groups.length > 0 && groups.find(g => g.id === formData.groups[0])?.name === "Sales Director"}
              onChange={(checked) => {
                if (checked) {
                  // Show confirmation dialog when turning ON
                  setPendingSalesDirectorChange(true);
                  setConfirmSalesDirector(true);
                } else {
                  // No confirmation needed when turning OFF
                  setFormData(prev => ({ ...prev, groups: [] }));
                }
              }}
            />
            <SettingRow 
              label="Regional Manager" 
              icon={<UserRound size={19} strokeWidth={2} />}
              iconColor="#2563eb"
              checked={formData.groups.length > 0 && groups.find(g => g.id === formData.groups[0])?.name === "Regional Manager"}
              onChange={(checked) => {
                if (checked) {
                  const group = groups.find(g => g.name === "Regional Manager");
                  if (group) {
                    setFormData(prev => ({ ...prev, groups: [group.id] }));
                  }
                } else {
                  setFormData(prev => ({ ...prev, groups: [] }));
                }
              }}
            />
            <SettingRow 
              label="Territory Manager" 
              icon={<UserRound size={19} strokeWidth={2} />}
              iconColor="#f59e0b"
              checked={formData.groups.length > 0 && groups.find(g => g.id === formData.groups[0])?.name === "Territory Manager"}
              onChange={(checked) => {
                if (checked) {
                  const group = groups.find(g => g.name === "Territory Manager");
                  if (group) {
                    setFormData(prev => ({ ...prev, groups: [group.id] }));
                  }
                } else {
                  setFormData(prev => ({ ...prev, groups: [] }));
                }
              }}
            />
            <SettingRow 
              label="Sales Rep" 
              icon={<User size={19} strokeWidth={2} />}
              iconColor="#16a34a"
              checked={formData.groups.length > 0 && groups.find(g => g.id === formData.groups[0])?.name === "Sales Rep"}
              onChange={(checked) => {
                if (checked) {
                  const group = groups.find(g => g.name === "Sales Rep");
                  if (group) {
                    setFormData(prev => ({ ...prev, groups: [group.id] }));
                  }
                } else {
                  setFormData(prev => ({ ...prev, groups: [] }));
                }
              }}
            />
          </CardContent>
        </Card>
      </Box>

      {/* -----------------------------------------------------------------------------Mobile Save Button - Bottom of Page */}
      {isMobile && (
        <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <DisabledButtonWrapper 
            isDisabled={(isReadOnly || !canSave) && !saving}
            tooltipText={!canSave ? "Please complete all required fields" : "Read-only mode: This action is disabled"}
          >
            <Button
              startIcon={saving ? <CircularProgress size={16} thickness={5} color="inherit" /> : <SaveIcon />}
              variant="outlined"
              color="success"
              onClick={handleSubmit}
              disabled={isReadOnly || !canSave || saving}
              fullWidth
              sx={{ 
                whiteSpace: "nowrap",
                opacity: (isReadOnly || !canSave) ? 0.6 : 1,
                cursor: saving ? "wait" : (isReadOnly || !canSave) ? "not-allowed" : "pointer",
                visibility: 'visible',
                display: 'inline-flex',
              }}
            >
              {saving ? "Saving..." : "Save User"}
            </Button>
          </DisabledButtonWrapper>
        </Box>
      )}

      {/* -----------------------------------------------------------------------Sales Director Confirmation Dialog */}
      <ConfirmDialog
        open={confirmSalesDirector}
        onClose={() => {
          setConfirmSalesDirector(false);
          setPendingSalesDirectorChange(false);
        }}
        onConfirm={() => {
          if (pendingSalesDirectorChange) {
            const group = groups.find(g => g.name === "Sales Director");
            if (group) {
              setFormData(prev => ({ ...prev, groups: [group.id] }));
            }
          }
          setConfirmSalesDirector(false);
          setPendingSalesDirectorChange(false);
        }}
        title="Confirm Sales Director Role"
        message="Are you sure you want to make this user a Sales Director?"
        confirmText="Yes, Confirm"
        cancelText="Cancel"
      />

      {/* ---------------------------------------------------------------------------------------Admin Warning Dialog */}
      <ConfirmDialog
        open={showAdminWarning}
        onClose={() => setShowAdminWarning(false)}
        onConfirm={() => setShowAdminWarning(false)}
        title="Admin Role Already Assigned"
        message="ADMIN role is already assigned. You cannot have more than one admin."
        confirmText="OK"
        cancelText={null}
        severity="warning"
      />
    </Box>
</Box>
  );
}

/* ------------------------------------------------------------------------------- SMALL COMPONENTS -------- */

function SettingRow({
  label,
  checked,
  onChange,
  icon = null,
  iconColor = "text.secondary",
}) {
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        minHeight: 44,
        py: 0.55,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
        {icon && (
          <Box sx={{ display: "flex", alignItems: "center", color: iconColor, flexShrink: 0 }}>
            {icon}
          </Box>
        )}
        <Typography variant="body2">{label}</Typography>
      </Box>
      <Switch 
        checked={checked || false} 
        onChange={(e) => onChange && onChange(e.target.checked)}
      />
    </Box>
  );
}
